import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { scheduleDays, scheduleFromDays } from '@goaltree/backend/convex/lib/schedule'
import { habitViewing, taskViewing } from '@goaltree/backend/convex/lib/viewingContext'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@goaltree/ui/components/alert-dialog'
import { Calendar } from '@goaltree/ui/components/calendar'
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '@goaltree/ui/components/popover'
import { Sheet, SheetContent } from '@goaltree/ui/components/sheet'
import { enUS, es, fr, ja, pt, zhCN } from '@goaltree/ui/lib/locales'
import { profileUrl } from '@goaltree/ui/lib/social-share'
import { cn } from '@goaltree/ui/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { useAction, useMutation, useQuery } from 'convex/react'
import i18next from 'i18next'
import {
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  Clock,
  Flag,
  Heart,
  Loader2,
  Plus,
  RefreshCw,
  SkipForward,
  Sun,
  Trash2,
  TrendingUp,
  UserRound,
  X,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
  AskCoachComposer,
  AskCoachThread,
  useAskChips,
  useCoachPersona,
} from '@/components/ask-coach'
import { ConvertToEventDialog } from '@/components/convert-to-event'
import {
  DetailActions,
  DetailKindLabel,
  DetailMenu,
  type DetailMenuItem,
  DetailPartOf,
  DetailPrimaryAction,
  DetailRow,
  DetailRows,
} from '@/components/detail-panel'
import { MarkdownContent } from '@/components/markdown-content'
import { RecursDaysChips } from '@/components/recurs-days-chips'
import { SavedHint, useSavedHint } from '@/components/saved-hint'
import { TaskDurationPicker } from '@/components/task-duration-picker'
import { TaskLocationRow } from '@/components/task-location'
import { UrgentFlag } from '@/components/task-meta'
import { TaskNoteField } from '@/components/task-note-field'
import { TaskStatusIcon } from '@/components/task-status-icon'
import { TimeField } from '@/components/time-field'
import { UpgradeDialog } from '@/components/upgrade-dialog'
import { useIsPro } from '@/hooks/use-is-pro'
import { type MustDoDay, useMustDo } from '@/hooks/use-must-do'
import { useSetTaskStatus } from '@/hooks/use-set-task-status'
import { useStickToBottom } from '@/hooks/use-stick-to-bottom'
import { useTaskExplain } from '@/hooks/use-task-explain'
import type { TodayTask } from '@/hooks/use-today-tasks'
import { caretOffsetFromPoint } from '@/lib/caret'
import { CATEGORY_COLORS } from '@/lib/category-color'
import { useReportViewing } from '@/lib/coach-call-store'
import { todayKey } from '@/lib/day'
import {
  optimisticAllDay,
  optimisticDueDate,
  optimisticDuration,
  optimisticRecurs,
  optimisticRename,
  patchNode,
} from '@/lib/nodes-optimistic'
import { periodLabel } from '@/lib/schedule'
import {
  addUrgentToTitle,
  parseTaskFields,
  parseTaskTitle,
  removeUrgentFromTitle,
  taskDescription,
  taskTags,
  taskTitle,
} from '@/lib/tags'
import {
  coerceEnd,
  isDueTodayOrOverdue,
  isTaskOverdue,
  nextHourEnd,
  nextHourStart,
  shiftTime,
  toMinutes,
} from '@/lib/time'
import { handleUsageLimit } from '@/lib/usage-limit'
import { goalUrl } from '@/lib/utils'

const EFFORT_CLASSES: Record<string, string> = {
  low: 'text-green-500 dark:text-green-400',
  medium: 'text-yellow-500 dark:text-yellow-400',
  high: 'text-red-500 dark:text-red-400',
}

const DATE_FNS_LOCALES: Record<string, typeof enUS> = { en: enUS, es, fr, ja, pt, zh: zhCN }
const RECURS_PERIODS = [
  'daily',
  'everyOtherDay',
  'weekly',
  'monthly',
  'weekend',
  'weekday',
  'days',
] as const
type RecursPeriod = (typeof RECURS_PERIODS)[number]
// Shown when "Specific days" is picked for a task that doesn't repeat yet.
const DEFAULT_DAYS = [1, 3, 5]

type Props = {
  task: (TodayTask & { localDone: boolean }) | null
  onClose: () => void
  onComplete: (taskId: Id<'nodes'>) => void
  onUncomplete?: (taskId: Id<'nodes'>) => void
  // Skip for today ("not today"); the caller offers optional reasons afterwards.
  onSkip?: (taskId: Id<'nodes'>, reason?: string) => void
}

type LikerEntry = {
  sessionId: string
  userId: string | null
  name: string | null
  image: string | null
  handle: string | null
}

/** Rebuild the editable `title #tags /// description /// urgent` string from structured fields. */
function editableTaskString(t: {
  title: string
  description?: string | null
  tags?: string[] | null
  effort?: 'low' | 'medium' | 'high'
  superUrgent?: boolean
}): string {
  const tagStr = taskTags(t)
    .map((tag) => `#${tag}`)
    .join(' ')
  let s = tagStr ? `${taskTitle(t)} ${tagStr}` : taskTitle(t)
  const desc = taskDescription(t)
  if (desc) s += ` /// ${desc}`
  if (t.effort === 'high') s += t.superUrgent ? ' /// super urgent' : ' /// urgent'
  return s
}

function LikerAvatar({ liker }: { liker: LikerEntry }) {
  const { t } = useTranslation()
  const initials =
    liker.name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) ?? ''
  const imageUrl = liker.image ?? null
  const href = liker.handle ? profileUrl(liker.handle) : null
  const key = liker.userId ?? liker.sessionId

  const avatar = (
    <div
      key={key}
      title={liker.name ?? t('common.anonymous')}
      className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-background overflow-hidden"
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={liker.name ?? ''}
          className="h-full w-full object-cover"
          referrerPolicy="no-referrer"
        />
      ) : initials ? (
        <div className="flex h-full w-full items-center justify-center bg-primary/20 text-[9px] font-bold text-primary-text">
          {initials}
        </div>
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-muted">
          <UserRound className="h-4 w-4 text-muted-foreground/50" />
        </div>
      )}
    </div>
  )

  return href ? (
    <a
      key={key}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="transition-transform hover:scale-110 active:scale-95"
    >
      {avatar}
    </a>
  ) : (
    <span key={key} className="cursor-default">
      {avatar}
    </span>
  )
}

function TaskLikers({ taskId }: { taskId: string }) {
  const { t } = useTranslation()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const likesApi = api as any
  const likers = useQuery(likesApi.taskLikes.getTaskLikers, { taskId }) as LikerEntry[] | undefined

  if (!likers || likers.length === 0) return null

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        {likers.map((liker) => (
          <LikerAvatar key={liker.userId ?? liker.sessionId} liker={liker} />
        ))}
      </div>
      <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
        <Heart className="h-2.5 w-2.5 inline-block mr-1" /> {likers.length}{' '}
        {likers.length === 1 ? t('common.like') : t('common.likes')}
      </p>
    </div>
  )
}

function taskSheetViewing(task: TodayTask & { localDone: boolean }) {
  const done = task.localDone || task.completedAt !== null
  if (task.recurs || task.recursPeriod)
    return habitViewing(
      task.taskId,
      {
        title: task.title,
        period: task.recursPeriod,
        recursDays: task.recursDays,
        doneToday: done,
      },
      'sheet',
    )
  return taskViewing(task.taskId, {
    title: task.title,
    note: task.description,
    dueDate: task.dueDate,
    status: done ? 'done' : task.status,
    goalTitle: task.isInbox ? null : task.goalTitle,
  })
}

export function TaskDetailSheet({ task, onClose, onComplete, onUncomplete, onSkip }: Props) {
  const { t } = useTranslation()
  useReportViewing(() => (task ? taskSheetViewing(task) : null))
  const navigate = useNavigate()
  const setTaskStatus = useSetTaskStatus()
  const renameNode = useMutation(api.nodes.rename).withOptimisticUpdate(optimisticRename)
  const updateEffort = useMutation(api.nodes.updateTaskEffort).withOptimisticUpdate(
    (localStore, { taskId, effort, superUrgent }) =>
      patchNode(localStore, taskId, { effort, superUrgent: superUrgent ? true : null }),
  )
  const updateDueDate = useMutation(api.nodes.updateTaskDueDate).withOptimisticUpdate(
    optimisticDueDate,
  )
  const updateRecursPeriod = useMutation(api.nodes.updateRecursPeriod).withOptimisticUpdate(
    optimisticRecurs,
  )
  const updateTaskDuration = useMutation(api.nodes.updateTaskDuration).withOptimisticUpdate(
    optimisticDuration,
  )
  const mustDo = useMustDo()
  // The sheet holds a snapshot of the task, so track the Must do day locally.
  const [mustLocal, setMustLocal] = useState<{ id: string; day: MustDoDay | null } | null>(null)
  const removeNode = useMutation(api.nodes.remove).withOptimisticUpdate(
    (localStore, { nodeId }) => {
      // Drop it from the Today surface immediately so confirming delete feels instant.
      const localDate = todayKey()
      const ctx = localStore.getQuery(api.dailySurface.getTodayWithContext, { localDate })
      if (ctx) {
        localStore.setQuery(
          api.dailySurface.getTodayWithContext,
          { localDate },
          { ...ctx, tasks: ctx.tasks.filter((t) => t.taskId !== nodeId) },
        )
      }
    },
  )

  const [renaming, setRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [localTitle, setLocalTitle] = useState('')
  const titleHint = useSavedHint()
  const [localDesc, setLocalDesc] = useState<string | null>(null)
  const [noteKey, setNoteKey] = useState(0)
  const [localFullTitle, setLocalFullTitle] = useState('')
  const renameRef = useRef<HTMLTextAreaElement>(null)
  const tapPositionRef = useRef<{ x: number; y: number } | null>(null)
  const renameTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [locallyDone, setLocallyDone] = useState(false)
  const [inProgress, setInProgress] = useState(false)
  const [isUrgent, setIsUrgent] = useState(false)
  const [isSuper, setIsSuper] = useState(false)
  const [localDueDate, setLocalDueDate] = useState<number | undefined>()
  const [localRecursPeriod, setLocalRecursPeriod] = useState<RecursPeriod | null>(null)
  const [localRecurrenceDay, setLocalRecurrenceDay] = useState<number>(1)
  const [localDays, setLocalDays] = useState<number[]>([])
  const [daysOpen, setDaysOpen] = useState(false)
  const [dueDateOpen, setDueDateOpen] = useState(false)
  const [recursOpen, setRecursOpen] = useState(false)
  const [pendingPromote, setPendingPromote] = useState(false)
  const [convertOpen, setConvertOpen] = useState(false)
  const [promoting, setPromoting] = useState(false)
  const promoteInboxTaskToGoal = useAction(api.voice.promoteInboxTaskToGoal)
  const [timeStr, setTimeStr] = useState('09:00')
  const [recurrenceTimeStr, setRecurrenceTimeStr] = useState('09:00')
  const [recurrenceEndTimeStr, setRecurrenceEndTimeStr] = useState('10:00')
  const [dueEndTimeStr, setDueEndTimeStr] = useState(nextHourEnd())
  const [localAllDay, setLocalAllDay] = useState(false)
  const [localMinutes, setLocalMinutes] = useState<number | undefined>()
  const [selectedDay, setSelectedDay] = useState<Date | undefined>()
  const setTaskAllDay = useMutation(api.nodes.setTaskAllDay).withOptimisticUpdate(optimisticAllDay)

  function navigateToGoal(search?: {
    openMilestone: string
    focusNode?: string
    focusTask?: string
  }) {
    if (!task?.goalId) return
    onClose()
    navigate({
      to: '/goals/$goalId',
      params: { goalId: goalUrl(task.goalTitle, task.goalId).replace('/goals/', '') },
      search,
    })
  }

  const color = task ? (CATEGORY_COLORS[task.category?.toLowerCase()] ?? '#6b6b6b') : '#6b6b6b'
  const isDone = task ? task.localDone || task.completedAt !== null : false
  const { isPro } = useIsPro()
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const chat = useTaskExplain()
  const { ask, isLoading, streamedText, isStreaming, reset } = chat
  const askChips = useAskChips('task')
  const { voice: coachVoice, name: coachName } = useCoachPersona(!!task)
  const responseRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (renameTimerRef.current) {
      clearTimeout(renameTimerRef.current)
      renameTimerRef.current = null
    }
    if (task?.title) {
      if (task.isInbox) {
        setLocalFullTitle(editableTaskString(task))
        setLocalTitle(taskTitle(task))
      } else {
        setLocalTitle(task.title)
        setLocalDesc(task.description ?? null)
      }
    }
    reset()
    setRenaming(false)
    setLocallyDone(false)
    setInProgress(task?.status === 'in-progress')
    setIsUrgent(task?.effort === 'high')
    setIsSuper(!!task?.superUrgent)
    const hasRecurring = !!(task?.recursPeriod || task?.recurs)
    setLocalRecursPeriod(
      (task?.recursPeriod as RecursPeriod | undefined) ?? (task?.recurs ? 'daily' : null),
    )
    setLocalRecurrenceDay(task?.recurrenceDay ?? 1)
    setLocalDays(scheduleDays(task?.recursPeriod, task?.recurrenceDay, task?.recursDays))
    setDaysOpen(false)
    setLocalDueDate(hasRecurring ? undefined : (task?.dueDate ?? undefined))
    setLocalAllDay(!!task?.allDay)
    setLocalMinutes(task?.durationMinutes ?? undefined)
    if (!hasRecurring && task?.dueDate) {
      const d = new Date(task.dueDate)
      setSelectedDay(d)
      const start = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
      setTimeStr(start)
      const endMins = d.getHours() * 60 + d.getMinutes() + (task?.durationMinutes ?? 60)
      setDueEndTimeStr(
        `${String(Math.floor(endMins / 60) % 24).padStart(2, '0')}:${String(endMins % 60).padStart(2, '0')}`,
      )
    } else {
      setSelectedDay(undefined)
      setTimeStr(nextHourStart())
      setDueEndTimeStr(nextHourEnd())
    }
    const startTime = task?.recurrenceTime || '09:00'
    setRecurrenceTimeStr(startTime)
    const [sh = 9, sm = 0] = startTime.split(':').map(Number)
    const endMins = sh * 60 + sm + (task?.durationMinutes ?? 60)
    setRecurrenceEndTimeStr(
      `${String(Math.floor(endMins / 60) % 24).padStart(2, '0')}:${String(endMins % 60).padStart(2, '0')}`,
    )
  }, [task?.taskId])

  const onResponseScroll = useStickToBottom(responseRef, streamedText, chat.pendingQuestion)

  useEffect(() => {
    const el = renameRef.current
    if (!el) return
    const size = () => {
      el.style.height = 'auto'
      el.style.height = `${el.scrollHeight}px`
    }
    size()
    // Re-measure after layout settles (open + keyboard viewport resize).
    requestAnimationFrame(size)
    const timer = setTimeout(size, 120)
    el.focus()
    const pos = tapPositionRef.current
    if (pos) {
      tapPositionRef.current = null
      const offset = caretOffsetFromPoint(el, pos.x, pos.y)
      el.setSelectionRange(offset, offset)
    } else {
      el.setSelectionRange(el.value.length, el.value.length)
    }
    return () => clearTimeout(timer)
  }, [renaming])

  const effortLabel: Record<string, string> = {
    low: t('today.effortLow'),
    medium: t('today.effortMedium'),
    high: t('today.effortHigh'),
  }

  const locale =
    DATE_FNS_LOCALES[i18next.language.split('-')[0] as keyof typeof DATE_FNS_LOCALES] ?? enUS

  function clearRecurring() {
    setLocalRecursPeriod(null)
    updateRecursPeriod({
      taskId: task!.taskId,
      recursPeriod: undefined,
      recurrenceTzOffset: new Date().getTimezoneOffset(),
    }).catch(() => {})
  }

  function handleDaySelect(date: Date | undefined) {
    if (!task) return
    const target = date ?? selectedDay
    if (!target) return
    const [h = 9, m = 0] = timeStr.split(':').map(Number)
    const d = new Date(target)
    d.setHours(h, m, 0, 0)
    setSelectedDay(d)
    setLocalDueDate(d.getTime())
    updateDueDate({ taskId: task.taskId, dueDate: d.getTime() }).catch(() => {})
    if (localRecursPeriod) clearRecurring()
    setDueDateOpen(false)
  }

  function handleTimeChange(newTime: string) {
    if (!task) return
    setTimeStr(newTime)
    if (selectedDay) {
      const [h = 9, m = 0] = newTime.split(':').map(Number)
      const d = new Date(selectedDay)
      d.setHours(h, m, 0, 0)
      setLocalDueDate(d.getTime())
      updateDueDate({ taskId: task.taskId, dueDate: d.getTime() }).catch(() => {})
    }
  }

  // End-time for a one-off dated event → rewrites durationMinutes.
  function handleDueEndChange(v: string) {
    if (!task) return
    const startStr = timeStr || nextHourStart()
    const newEnd = coerceEnd(startStr, v)
    setDueEndTimeStr(newEnd)
    updateTaskDuration({
      taskId: task.taskId,
      durationMinutes: Math.max(15, toMinutes(newEnd) - toMinutes(startStr)),
    }).catch(() => {})
  }

  function toggleAllDay() {
    if (!task) return
    const next = !localAllDay
    setLocalAllDay(next)
    setTaskAllDay({ taskId: task.taskId, allDay: next }).catch(() => setLocalAllDay(!next))
    if (next) return
    // Switching to timed → default the range to the next full hour.
    const start = nextHourStart()
    setDueEndTimeStr(nextHourEnd())
    handleTimeChange(start)
    updateTaskDuration({ taskId: task.taskId, durationMinutes: 60 }).catch(() => {})
  }

  function handleDatePreset(preset: 'today' | 'tomorrow' | 'thisWeekend' | 'nextWeek') {
    if (!task) return
    const d = new Date()
    switch (preset) {
      case 'tomorrow':
        d.setDate(d.getDate() + 1)
        break
      case 'thisWeekend': {
        const day = d.getDay()
        d.setDate(d.getDate() + (day === 6 ? 0 : 6 - day))
        break
      }
      case 'nextWeek': {
        const day = d.getDay()
        d.setDate(d.getDate() + (day === 0 ? 1 : 8 - day))
        break
      }
    }
    const [h = 9, m = 0] = timeStr.split(':').map(Number)
    d.setHours(h, m, 0, 0)
    setSelectedDay(d)
    setLocalDueDate(d.getTime())
    updateDueDate({ taskId: task.taskId, dueDate: d.getTime() }).catch(() => {})
    if (localRecursPeriod) clearRecurring()
    setDueDateOpen(false)
  }

  function dueLabel(ms: number) {
    const d = new Date(ms)
    const dateStr = d.toLocaleDateString(i18next.language, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })
    if (localAllDay) return dateStr
    const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    // Show from–to once the range spans more than an hour.
    if (toMinutes(dueEndTimeStr) - toMinutes(time) > 60)
      return `${dateStr}, ${time} – ${dueEndTimeStr}`
    return `${dateStr}, ${time}`
  }

  function ordinal(n: number) {
    if (!i18next.language.startsWith('en')) return String(n)
    try {
      const r = new Intl.PluralRules(i18next.language, { type: 'ordinal' }).select(n)
      const s: Record<string, string> = { one: 'st', two: 'nd', few: 'rd', other: 'th' }
      return `${n}${s[r] ?? 'th'}`
    } catch {
      return String(n)
    }
  }

  function toggleUrgent() {
    if (!task) return
    // normal -> urgent -> super urgent -> normal
    const nextUrgent = !isUrgent || !isSuper
    const nextSuper = isUrgent && !isSuper
    const prev = { urgent: isUrgent, sup: isSuper }
    setIsUrgent(nextUrgent)
    setIsSuper(nextSuper)
    const currentFull = removeUrgentFromTitle(localFullTitle || task.title)
    const newFull = nextUrgent ? addUrgentToTitle(currentFull, nextSuper) : currentFull
    setLocalFullTitle(newFull)
    if (renaming) setRenameValue(newFull)
    const effort = nextUrgent ? ('high' as const) : ('low' as const)
    const { title, description, tags } = parseTaskFields(newFull)
    renameNode({ nodeId: task.taskId, title, description, tags }).catch(() => {})
    updateEffort({ taskId: task.taskId, effort, superUrgent: nextSuper }).catch(() => {
      setIsUrgent(prev.urgent)
      setIsSuper(prev.sup)
    })
  }

  function toggleInProgress() {
    if (!task) return
    const next = inProgress ? 'backlog' : 'in-progress'
    setInProgress(!inProgress)
    setTaskStatus({ taskId: task.taskId, status: next }).catch(() => setInProgress(inProgress))
  }

  function applyDays(days: number[]) {
    if (!task) return
    const s = scheduleFromDays(days)
    setLocalDays(days)
    setLocalRecursPeriod(s.recursPeriod)
    if (s.recurrenceDay !== undefined) setLocalRecurrenceDay(s.recurrenceDay)
    updateRecursPeriod({
      taskId: task.taskId,
      recursPeriod: 'days',
      recursDays: days,
      recurrenceTzOffset: new Date().getTimezoneOffset(),
      ...(localRecursPeriod && { recurrenceTime: recurrenceTimeStr }),
    }).catch(() => {})
    if (localDueDate) {
      setLocalDueDate(undefined)
      updateDueDate({ taskId: task.taskId, dueDate: undefined }).catch(() => {})
    }
  }

  function selectPeriod(p: RecursPeriod) {
    if (!task) return
    setDaysOpen(p === 'days')
    if (p === 'days') {
      if (localRecursPeriod)
        setLocalDays(scheduleDays(localRecursPeriod, localRecurrenceDay, localDays))
      else applyDays(DEFAULT_DAYS)
      return
    }
    const wasSet = !!localRecursPeriod
    setLocalRecursPeriod(p)
    updateRecursPeriod({
      taskId: task.taskId,
      recursPeriod: p,
      recurrenceTzOffset: new Date().getTimezoneOffset(),
      ...(wasSet && { recurrenceTime: recurrenceTimeStr }),
      ...((p === 'weekly' || p === 'monthly') && { recurrenceDay: localRecurrenceDay }),
    }).catch(() => {})
    if (localDueDate) {
      setLocalDueDate(undefined)
      updateDueDate({ taskId: task.taskId, dueDate: undefined }).catch(() => {})
    }
  }

  function selectRecurrenceDay(d: number) {
    if (!task || (localRecursPeriod !== 'weekly' && localRecursPeriod !== 'monthly')) return
    setLocalRecurrenceDay(d)
    updateRecursPeriod({
      taskId: task.taskId,
      recursPeriod: localRecursPeriod,
      recurrenceDay: d,
      recurrenceTime: recurrenceTimeStr,
      recurrenceTzOffset: new Date().getTimezoneOffset(),
    }).catch(() => {})
  }

  function changeRecurrenceStart(v: string) {
    if (!task) return
    const newStart = v || '09:00'
    setRecurrenceTimeStr(newStart)
    const startMin = toMinutes(newStart)
    // Keep a later end; otherwise coerce it to 15 min after the new start.
    const end =
      recurrenceEndTimeStr && toMinutes(recurrenceEndTimeStr) > startMin
        ? recurrenceEndTimeStr
        : shiftTime(newStart, 15)
    setRecurrenceEndTimeStr(end)
    updateRecursPeriod({
      taskId: task.taskId,
      recursPeriod: localRecursPeriod ?? undefined,
      recurrenceTime: newStart,
      recurrenceTzOffset: new Date().getTimezoneOffset(),
    }).catch(() => {})
    updateTaskDuration({
      taskId: task.taskId,
      durationMinutes: Math.max(15, toMinutes(end) - startMin),
    }).catch(() => {})
  }

  function changeRecurrenceEnd(v: string) {
    if (!task) return
    const startStr = recurrenceTimeStr || '09:00'
    const newEnd = coerceEnd(startStr, v)
    setRecurrenceEndTimeStr(newEnd)
    updateTaskDuration({
      taskId: task.taskId,
      durationMinutes: Math.max(0, toMinutes(newEnd) - toMinutes(startStr)),
    }).catch(() => {})
  }

  function handleAsk(text: string) {
    if (!task || !text.trim() || isLoading || isStreaming) return
    if (!isPro) {
      setUpgradeOpen(true)
      return
    }
    ask(task.title, task.goalTitle, text, task.taskId)
  }

  const parsed = task?.isInbox ? parseTaskFields(localFullTitle || task.title) : null
  const cleanDesc = parsed?.description ?? null
  const tags = parsed ? parsed.tags : (task?.tags ?? [])
  const valueBtn =
    'flex min-w-0 cursor-pointer items-center gap-1.5 rounded-sm outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50'

  const savedMustDay: MustDoDay | null = task ? (task.mustDo ? 'today' : null) : null
  const mustDay = task && mustLocal?.id === (task.taskId as string) ? mustLocal.day : savedMustDay
  // Tapping the active day clears it; the other day moves the commitment.
  function commitMust(day: MustDoDay) {
    if (!task) return
    const next = mustDay === day ? null : day
    const prev = mustDay
    setMustLocal({ id: task.taskId as string, day: next })
    void mustDo(task.taskId, next).then(
      (ok) => !ok && setMustLocal({ id: task.taskId as string, day: prev }),
    )
  }

  const menuItems: DetailMenuItem[] = task
    ? [
        ...(task.isInbox && !isDone
          ? [
              {
                key: 'promote',
                label: t('common.promoteToGoal'),
                icon: <TrendingUp className="h-4 w-4" />,
                onSelect: () => setPendingPromote(true),
              },
            ]
          : []),
        ...(!isDone && !locallyDone
          ? [
              {
                key: 'mustToday',
                label: t('mustDo.doToday'),
                icon: <CalendarCheck className="h-4 w-4" />,
                inline: true,
                active: mustDay === 'today',
                onSelect: () => commitMust('today'),
              },
              {
                key: 'mustTomorrow',
                label: t('mustDo.doTomorrow'),
                icon: <CalendarClock className="h-4 w-4" />,
                inline: true,
                active: mustDay === 'tomorrow',
                onSelect: () => commitMust('tomorrow'),
              },
            ]
          : []),
        ...(onSkip && !isDone && !locallyDone
          ? [
              {
                key: 'skip',
                label: t('today.skipNotToday'),
                icon: <SkipForward className="h-4 w-4" />,
                onSelect: () => {
                  onSkip(task.taskId)
                  onClose()
                },
              },
            ]
          : []),
        ...(!isDone && !locallyDone && !task.recurs && !task.recursPeriod
          ? [
              {
                key: 'convertEvent',
                label: t('events.convertToEvent'),
                icon: <CalendarDays className="h-4 w-4" />,
                inline: true,
                onSelect: () => setConvertOpen(true),
              },
            ]
          : []),
        {
          key: 'delete',
          label: t('inbox.deleteEllipsis'),
          icon: <Trash2 className="h-4 w-4" />,
          destructive: true,
          inline: true,
          onSelect: () => {
            removeNode({ nodeId: task.taskId }).catch(() => toast.error('Failed to delete task'))
            onClose()
          },
        },
      ]
    : []

  const dueDateBody = (
    <>
      <div className="flex w-full flex-wrap justify-center gap-1 px-3 pt-2 pb-0">
        {(['today', 'tomorrow', 'thisWeekend', 'nextWeek'] as const).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => handleDatePreset(p)}
            className="cursor-pointer rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            {t(`inbox.datePreset.${p}`)}
          </button>
        ))}
      </div>
      <Calendar
        mode="single"
        disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0)) }}
        selected={selectedDay}
        defaultMonth={selectedDay ?? new Date()}
        onSelect={handleDaySelect}
        locale={locale}
        classNames={{
          range_start: '',
          range_middle: '',
          range_end: '',
          root: 'w-full',
          today: 'rounded-(--cell-radius) bg-muted text-foreground',
        }}
      />
      <div className="flex w-full items-center justify-center gap-2 border-t border-border px-3 pt-3 pb-1">
        <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <div className={cn('flex items-center gap-1.5', localAllDay && 'opacity-40')}>
          <input
            type="time"
            lang="en-GB"
            value={timeStr}
            onChange={(e) => handleTimeChange(e.target.value)}
            className="cursor-pointer rounded bg-transparent px-1 text-sm text-foreground outline-none transition-colors hover:bg-muted"
          />
          <span className="text-muted-foreground/40 text-xs select-none">→</span>
          <input
            type="time"
            lang="en-GB"
            value={dueEndTimeStr}
            onChange={(e) => handleDueEndChange(e.target.value)}
            className="cursor-pointer rounded bg-transparent px-1 text-sm text-foreground outline-none transition-colors hover:bg-muted"
          />
        </div>
      </div>
      <div className="w-full px-3 pb-2">
        <button
          type="button"
          onClick={toggleAllDay}
          className={cn(
            'flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-[11px] font-medium transition-colors',
            localAllDay
              ? 'border-primary/40 bg-primary/10 text-primary-text hover:bg-primary/15'
              : 'border-border/60 text-muted-foreground hover:bg-muted hover:text-foreground',
          )}
        >
          <Sun className="h-3 w-3 shrink-0" />
          {t('inbox.allDay')}
        </button>
      </div>
    </>
  )

  const periodOption = (p: RecursPeriod) => (
    <span
      className={cn(
        'flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted',
        (p === 'days' ? daysOpen || localRecursPeriod === 'days' : localRecursPeriod === p)
          ? 'text-primary-text'
          : 'text-foreground',
      )}
    >
      <RefreshCw className="h-3 w-3" strokeWidth={2.5} />
      {t(`inbox.period.${p}`)}
    </span>
  )

  const dayOption = (d: number, label: string | number) => (
    <PopoverClose
      key={d}
      className="flex h-7 w-7 cursor-pointer items-center justify-center rounded"
      onClick={() => selectRecurrenceDay(d)}
    >
      <span
        className={cn(
          'flex h-7 w-7 items-center justify-center rounded text-xs font-medium transition-colors hover:bg-muted',
          localRecurrenceDay === d ? 'bg-primary text-primary-foreground' : 'text-foreground',
        )}
      >
        {label}
      </span>
    </PopoverClose>
  )

  return (
    <>
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} reason="ask-ai" />
      <Sheet open={!!task} onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="right" className="flex w-80 flex-col p-0 sm:max-w-sm">
          {task && (
            <div className="flex flex-1 flex-col overflow-hidden">
              {/* Top bar — pr clears the sheet's own close button */}
              <div className="flex shrink-0 items-center gap-2 pt-1.5 pr-11 pl-4">
                <DetailKindLabel>{t('inbox.taskLabel')}</DetailKindLabel>
                <DetailMenu
                  items={menuItems}
                  confirmTitle={t('common.deleteTaskTitle')}
                  confirmDescription={t('common.deleteTaskDesc', { title: task.title })}
                />
              </div>

              {/* Scrollable content */}
              <div
                ref={responseRef}
                onScroll={onResponseScroll}
                className="flex flex-1 min-w-0 flex-col gap-5 overflow-y-auto overflow-x-hidden px-4 pt-3 pb-4"
              >
                {/* Title + description */}
                <div className="flex shrink-0 flex-col gap-1.5">
                  {renaming ? (
                    <textarea
                      ref={renameRef}
                      rows={1}
                      value={renameValue}
                      onChange={(e) => {
                        const val = e.target.value
                        setRenameValue(val)
                        e.target.style.height = 'auto'
                        e.target.style.height = `${e.target.scrollHeight}px`
                        if (task.isInbox) {
                          {
                            const p = parseTaskFields(val)
                            setIsUrgent(p.effort === 'high')
                            setIsSuper(p.superUrgent)
                          }
                          if (renameTimerRef.current) clearTimeout(renameTimerRef.current)
                          renameTimerRef.current = setTimeout(() => {
                            const trimmed = val.trim()
                            if (!trimmed) return
                            const {
                              title,
                              description,
                              tags,
                              effort: parsedEffort,
                              superUrgent: parsedSuper,
                            } = parseTaskFields(trimmed)
                            setLocalFullTitle(trimmed)
                            setLocalTitle(title)
                            void titleHint.track(
                              renameNode({
                                nodeId: task.taskId,
                                title,
                                description,
                                tags,
                              }),
                            )
                            const effectiveEffort = parsedEffort ?? 'low'
                            setIsUrgent(effectiveEffort === 'high')
                            setIsSuper(parsedSuper)
                            updateEffort({
                              taskId: task.taskId,
                              effort: effectiveEffort,
                              superUrgent: parsedSuper,
                            }).catch(() => {})
                          }, 500)
                        }
                      }}
                      onBlur={() => {
                        if (task.isInbox) {
                          if (renameTimerRef.current) {
                            clearTimeout(renameTimerRef.current)
                            renameTimerRef.current = null
                          }
                          const trimmed = renameValue.trim()
                          if (trimmed) {
                            const {
                              title,
                              description,
                              tags,
                              effort: parsedEffort,
                              superUrgent: parsedSuper,
                            } = parseTaskFields(trimmed)
                            setLocalFullTitle(trimmed)
                            setLocalTitle(title)
                            void titleHint.track(
                              renameNode({
                                nodeId: task.taskId,
                                title,
                                description,
                                tags,
                              }),
                            )
                            const effectiveEffort = parsedEffort ?? 'low'
                            setIsUrgent(effectiveEffort === 'high')
                            setIsSuper(parsedSuper)
                            updateEffort({
                              taskId: task.taskId,
                              effort: effectiveEffort,
                              superUrgent: parsedSuper,
                            }).catch(() => {})
                          }
                        } else {
                          const { title, description } = parseTaskTitle(renameValue)
                          if (title && (title !== localTitle || description)) {
                            setLocalTitle(title)
                            if (description) {
                              setLocalDesc(description)
                              setNoteKey((k) => k + 1)
                            }
                            void titleHint
                              .track(
                                renameNode({
                                  nodeId: task.taskId,
                                  title,
                                  ...(description && { description }),
                                }),
                              )
                              .then((ok) => {
                                if (!ok) setLocalTitle(localTitle)
                              })
                          }
                        }
                        setRenaming(false)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault()
                          e.currentTarget.blur()
                        }
                        if (e.key === 'Escape') {
                          if (task.isInbox && renameTimerRef.current) {
                            clearTimeout(renameTimerRef.current)
                            renameTimerRef.current = null
                          }
                          setRenaming(false)
                        }
                      }}
                      className={cn(
                        'min-h-9 w-full resize-none overflow-y-auto bg-transparent text-2xl font-bold leading-tight outline-none',
                        isDone ? 'text-foreground/50 line-through' : 'text-foreground',
                      )}
                    />
                  ) : (
                    <p
                      className={cn(
                        'text-2xl font-bold leading-tight',
                        isDone ? 'text-foreground/50 line-through' : 'text-foreground',
                      )}
                    >
                      {/* line-through on the <p> propagates onto the child;
                        the inner span adds the hover underline so both show
                        on hover, and only line-through shows while editing. */}
                      <span
                        className="cursor-text hover:underline decoration-dashed underline-offset-4"
                        onClick={(e) => {
                          tapPositionRef.current = { x: e.clientX, y: e.clientY }
                          if (task.isInbox) {
                            const seed = localFullTitle || task.title
                            setRenameValue(isUrgent ? addUrgentToTitle(seed, isSuper) : seed)
                          } else {
                            setRenameValue(localTitle)
                          }
                          setRenaming(true)
                        }}
                      >
                        {localTitle}
                      </span>
                    </p>
                  )}
                  <SavedHint status={titleHint.status} />
                  {!task.isInbox && (
                    <TaskNoteField
                      key={`${task.taskId}-${noteKey}`}
                      value={localDesc}
                      onChange={setLocalDesc}
                      onSave={(description) =>
                        renameNode({
                          nodeId: task.taskId,
                          title: localTitle || task.title,
                          description,
                        })
                      }
                    />
                  )}
                  {cleanDesc && (
                    <MarkdownContent
                      text={cleanDesc}
                      className="!text-muted-foreground/70 prose-p:!my-0"
                      linkChips
                    />
                  )}
                </div>

                <DetailPrimaryAction
                  kind="task"
                  done={isDone || locallyDone}
                  onClick={() => {
                    if (isDone) {
                      if (!onUncomplete) return
                      onUncomplete(task.taskId)
                      onClose()
                      return
                    }
                    if (locallyDone) return
                    setLocallyDone(true)
                    setTimeout(() => {
                      onComplete(task.taskId)
                      onClose()
                    }, 600)
                  }}
                />

                <DetailPartOf
                  color={color}
                  path={
                    task.isInbox || !task.goalTitle
                      ? []
                      : [
                          { title: task.goalTitle, onClick: () => navigateToGoal() },
                          ...(task.milestoneTitle && task.milestoneId
                            ? [
                                {
                                  title: task.milestoneTitle,
                                  onClick: () =>
                                    navigateToGoal({ openMilestone: task.milestoneId! }),
                                },
                              ]
                            : []),
                          ...(task.targetTitle && task.milestoneId
                            ? [
                                {
                                  title: task.targetTitle,
                                  onClick: () =>
                                    navigateToGoal({
                                      openMilestone: task.milestoneId!,
                                      ...(task.targetId
                                        ? { focusNode: task.targetId }
                                        : { focusTask: task.taskId }),
                                    }),
                                },
                              ]
                            : []),
                        ]
                  }
                />

                <DetailRows>
                  <DetailRow label={t('inbox.rowDue')}>
                    <Popover open={dueDateOpen} onOpenChange={setDueDateOpen}>
                      <PopoverTrigger
                        className={cn(
                          valueBtn,
                          !localDueDate
                            ? 'text-muted-foreground'
                            : isDueTodayOrOverdue(localDueDate) && 'text-red-500 dark:text-red-400',
                        )}
                      >
                        {localDueDate ? (
                          `${isTaskOverdue(localDueDate, localAllDay) ? `${t('inbox.overdue')} · ` : ''}${dueLabel(localDueDate)}`
                        ) : (
                          <>
                            {t('inbox.addDueDate')}
                            <Plus className="h-3 w-3" />
                          </>
                        )}
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-[min(20rem,92vw)] p-0"
                        side="bottom"
                        align="start"
                      >
                        {dueDateBody}
                      </PopoverContent>
                    </Popover>
                    {localDueDate && (
                      <button
                        type="button"
                        aria-label={t('common.clear', { defaultValue: 'Clear' })}
                        onClick={() => {
                          setLocalDueDate(undefined)
                          updateDueDate({ taskId: task.taskId, dueDate: undefined }).catch(() => {})
                        }}
                        className="cursor-pointer rounded-sm text-muted-foreground/60 outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50"
                      >
                        <X className="h-3 w-3" strokeWidth={2.5} />
                      </button>
                    )}
                  </DetailRow>
                  <TaskLocationRow taskId={task.taskId} location={task.location} />

                  {task.isInbox ? (
                    <DetailRow label={t('filter.groupPriority')} onClick={toggleUrgent}>
                      {isUrgent ? (
                        <UrgentFlag superUrgent={isSuper} />
                      ) : (
                        <Flag className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                      <span className={isUrgent ? 'text-red-500 dark:text-red-400' : undefined}>
                        {isSuper
                          ? t('filter.prioritySuperUrgent')
                          : isUrgent
                            ? t('filter.priorityUrgent')
                            : t('filter.priorityNormal')}
                      </span>
                    </DetailRow>
                  ) : (
                    <DetailRow label={t('filter.effort')}>
                      <span className={EFFORT_CLASSES[task.effort] ?? EFFORT_CLASSES.medium}>
                        {effortLabel[task.effort]}
                      </span>
                    </DetailRow>
                  )}

                  {!isDone && (localAllDay || (!localDueDate && !task.recurrenceTime)) && (
                    <DetailRow label={t('timeBudget.taskTime')}>
                      <TaskDurationPicker
                        value={localMinutes}
                        className={valueBtn}
                        onChange={(m) => {
                          const prev = localMinutes
                          setLocalMinutes(m)
                          updateTaskDuration({ taskId: task.taskId, durationMinutes: m }).catch(
                            () => setLocalMinutes(prev),
                          )
                        }}
                      />
                    </DetailRow>
                  )}

                  {!isDone && (
                    <DetailRow label={t('filter.status')} onClick={toggleInProgress}>
                      <TaskStatusIcon status={inProgress ? 'in_progress' : 'pending'} size={14} />
                      {inProgress ? t('goals.inProgress') : t('inbox.notStarted')}
                    </DetailRow>
                  )}

                  {task.isInbox && (
                    <DetailRow label={t('inbox.rowRepeats')}>
                      <Popover
                        open={recursOpen}
                        onOpenChange={(o) => {
                          setRecursOpen(o)
                          if (!o) setDaysOpen(false)
                        }}
                      >
                        <PopoverTrigger
                          className={cn(valueBtn, !localRecursPeriod && 'text-muted-foreground')}
                        >
                          {localRecursPeriod ? (
                            <>
                              {periodLabel(t, i18next.language, localRecursPeriod, localDays)}
                              {localRecursPeriod === 'weekly' &&
                                ` · ${new Intl.DateTimeFormat(i18next.language, { weekday: 'short' }).format(new Date(2025, 0, 5 + localRecurrenceDay))}`}
                              {localRecursPeriod === 'monthly' &&
                                ` · ${ordinal(localRecurrenceDay)}`}
                            </>
                          ) : (
                            t('inbox.repeatsNever')
                          )}
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-auto min-w-[140px] p-1"
                          side="bottom"
                          align="start"
                        >
                          {RECURS_PERIODS.map((p) =>
                            p === 'weekly' || p === 'monthly' || p === 'days' ? (
                              // Stays open so the day picker below can be used.
                              <button
                                key={p}
                                type="button"
                                className="w-full"
                                onClick={() => selectPeriod(p)}
                              >
                                {periodOption(p)}
                              </button>
                            ) : (
                              <PopoverClose
                                key={p}
                                className="w-full"
                                onClick={() => selectPeriod(p)}
                              >
                                {periodOption(p)}
                              </PopoverClose>
                            ),
                          )}
                          {(daysOpen || localRecursPeriod === 'days') && (
                            <RecursDaysChips
                              className="mt-1 border-t border-border/40 px-2 py-2"
                              days={localDays}
                              onChange={applyDays}
                            />
                          )}
                          {localRecursPeriod === 'weekly' && !daysOpen && (
                            <div className="mt-1 flex gap-0.5 border-t border-border/40 px-2 py-2">
                              {[0, 1, 2, 3, 4, 5, 6].map((d) =>
                                dayOption(
                                  d,
                                  new Intl.DateTimeFormat(i18next.language, {
                                    weekday: 'narrow',
                                  }).format(new Date(2025, 0, 5 + d)),
                                ),
                              )}
                            </div>
                          )}
                          {localRecursPeriod === 'monthly' && (
                            <div className="mt-1 grid w-[200px] grid-cols-7 gap-0.5 border-t border-border/40 px-2 py-2">
                              {Array.from({ length: 31 }, (_, i) => i + 1).map((d) =>
                                dayOption(d, d),
                              )}
                            </div>
                          )}
                          {localRecursPeriod && (
                            <PopoverClose className="w-full" onClick={clearRecurring}>
                              <span className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10">
                                <X className="h-3 w-3" strokeWidth={2.5} />
                                {t('inbox.removeRecurring')}
                              </span>
                            </PopoverClose>
                          )}
                        </PopoverContent>
                      </Popover>
                      {localRecursPeriod && (
                        <span className="flex items-center gap-0.5 tabular-nums">
                          <TimeField
                            ariaLabel={t('inbox.startTime')}
                            className="px-1 py-0.5 transition-colors hover:bg-muted"
                            value={recurrenceTimeStr}
                            onChange={changeRecurrenceStart}
                          />
                          <span className="text-muted-foreground/40">–</span>
                          <TimeField
                            ariaLabel={t('inbox.endTime')}
                            className="px-1 py-0.5 transition-colors hover:bg-muted"
                            value={recurrenceEndTimeStr}
                            onChange={changeRecurrenceEnd}
                          />
                        </span>
                      )}
                    </DetailRow>
                  )}

                  {tags.length > 0 && (
                    <DetailRow label={t('inbox.rowTags')}>
                      {tags.map((tag) => (
                        <span key={tag} className="text-muted-foreground">
                          #{tag}
                        </span>
                      ))}
                    </DetailRow>
                  )}
                </DetailRows>

                <DetailActions
                  items={menuItems}
                  confirmTitle={t('common.deleteTaskTitle')}
                  confirmDescription={t('common.deleteTaskDesc', { title: task.title })}
                />

                {/* Likers */}
                <TaskLikers taskId={task.taskId as string} />

                <AskCoachThread chat={chat} voice={coachVoice} chips={askChips} />
              </div>

              <AskCoachComposer
                subject="task"
                chips={askChips}
                chat={chat}
                coachName={coachName}
                onSend={handleAsk}
                resetKey={task?.taskId}
              />
            </div>
          )}
        </SheetContent>
      </Sheet>

      {task && (
        <ConvertToEventDialog
          taskId={task.taskId}
          title={task.title}
          dueDate={task.dueDate}
          open={convertOpen}
          onOpenChange={setConvertOpen}
          onConverted={onClose}
        />
      )}

      {task && (
        <AlertDialog
          open={pendingPromote}
          onOpenChange={(open) => !promoting && setPendingPromote(open)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('common.promoteToGoal')}</AlertDialogTitle>
              <AlertDialogDescription>
                "{task.title}" will become a standalone goal with an AI-generated plan. The inbox
                task will be removed.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={promoting}>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                disabled={promoting}
                onClick={async (e) => {
                  e.preventDefault()
                  setPromoting(true)
                  try {
                    const { goalId, title } = await promoteInboxTaskToGoal({ taskId: task.taskId })
                    onClose()
                    navigate({
                      to: '/goals/$goalId',
                      params: { goalId: goalUrl(title, goalId).replace('/goals/', '') },
                    })
                  } catch (err) {
                    if (!handleUsageLimit(err))
                      toast.error('Failed to promote task. Please try again.')
                  } finally {
                    setPromoting(false)
                    setPendingPromote(false)
                  }
                }}
              >
                {promoting ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  t('common.promoteToGoal')
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  )
}
